import { useAppContexts } from "hooks/use-context";
import useModal from "hooks/use-modal";
import BankingActions from "shared-components/banking-actions";
import { bankActions } from "types/club-banking";
import { SpendPermissions } from "types/roles-permissions";
import LiteTransfer from "./transfer-modal";
import FloatingActionBtn from "shared-components/floating-action-btn";
import { SnapActionSheet, SnapButton } from "suit";
import Divider from "shared-components/divider";
import LiteSendAch from "./send-ach";
import LiteSendCheck from "./send-check";
import DebitCards from "./debit-cards";
import CustomModal from "shared-components/modal";
import { TIER3 } from "../../../constants";

type AvailableBalanceCardProps = {
  totalAvailable: string;
};

function AvailableBalanceCard({ totalAvailable }: AvailableBalanceCardProps) {
  const { display, program, user } = useAppContexts();
  const { isOpen: isTransferOpen, toggle: transferToggle } = useModal();
  const { isOpen: isSendAchOpen, toggle: sendAchToggle } = useModal();
  const { isOpen: isSendCheckOpen, toggle: sendCheckToggle } = useModal();
  const { isOpen: isBankingOptionsOpen, toggle: toggleBankingOptions } =
    useModal();
  const { isOpen: isDebitCardsOpen, toggle: debitCardsToggle } = useModal();

  const canUpdateBanking =
    user?.checkSpendPermission(SpendPermissions.programBankUpdate) ?? false;

  const bankingActions: bankActions[] = [
    {
      icon: "cash-solid",
      label: "Deposit",
      onClick: () => {
        display?.setSelectedUnitAccount(
          program?.getProgramAccount()?.id ?? "",
          program?.getProgramAccount()?.name ?? "",
          program?.getProgramAccount()?.id ?? "",
          program?.organization?.nickname ||
            program?.organization?.legalName ||
            ""
        );
      },
      className: "mt-2 lg:hidden",
    },
    {
      icon: "switch-horizontal-solid",
      label: "Transfer",
      onClick: transferToggle,
      className: "mt-4",
    },
    {
      icon: "cash-solid",
      label: "Send Ach",
      onClick: sendAchToggle,
      className: "mt-4",
    },
    {
      icon: "mail-solid",
      label: "Send Check",
      onClick: sendCheckToggle,
      className: "mt-4",
    },
  ];

  if (program?.isLite && program.tier !== TIER3) {
    bankingActions.push({
      icon: "credit-card-solid",
      label: "Debit Cards",
      onClick: debitCardsToggle,
      className: "mt-4",
    });
  }

  return (
    <>
      <h1 className="text-lg font-bold">
        Total Available Balance (as of today)
      </h1>
      <Divider className="w-[80%]" />
      <div className="flex">
        <h1 className="text-4xl font-bold mt-5">
          {totalAvailable.split(".")[0]}
        </h1>
        <h1 className="text-2xl font-bold mt-5 self-end">
          .{totalAvailable.split(".")[1]}
        </h1>
      </div>
      <div
        className={`${
          canUpdateBanking && "lg:flex lg:flex-col hidden max-w-xs mt-5"
        }`}
      >
        {canUpdateBanking && (
          <>
            {bankingActions.map((actionItem, idx) => {
              return (
                <BankingActions
                  key={actionItem.label + idx}
                  icon={actionItem.icon}
                  label={actionItem.label}
                  action={actionItem.onClick}
                  className={actionItem.className}
                />
              );
            })}
          </>
        )}
      </div>
      {canUpdateBanking && (
        <FloatingActionBtn
          onClick={toggleBankingOptions}
          icon={"dots-horizontal-solid"}
        />
      )}
      {isBankingOptionsOpen && (
        <SnapActionSheet
          header="Banking Actions"
          onClick={toggleBankingOptions}
        >
          <Divider isVisibleOnMobile className="mt-0" />
          {bankingActions.map((actionItem, idx) => {
            return (
              <SnapButton
                key={idx}
                fullWidth
                icon={actionItem.icon}
                onClick={actionItem.onClick}
                className={`${actionItem.className} mx-6`}
                variant="secondary"
                size="md"
              >
                {actionItem.label}
              </SnapButton>
            );
          })}
        </SnapActionSheet>
      )}
      {isTransferOpen && (
        <LiteTransfer
          isTransferOpen={isTransferOpen}
          transferToggle={transferToggle}
        />
      )}
      {isSendAchOpen && (
        <LiteSendAch isOpen={isSendAchOpen} toggle={sendAchToggle} />
      )}
      {isSendCheckOpen && (
        <LiteSendCheck isOpen={isSendCheckOpen} toggle={sendCheckToggle} />
      )}
      {isDebitCardsOpen && (
        <CustomModal
          isOpen={isDebitCardsOpen}
          toggle={debitCardsToggle}
          title="Debit Cards"
        >
          <div className="modal-card">
            <DebitCards />
          </div>
        </CustomModal>
      )}
    </>
  );
}

export default AvailableBalanceCard;
